import React, { useState, useEffect } from "react";
import { Steps } from "intro.js-react";
import 'intro.js/introjs.css';
import "./attendee.css";
import AttendeeMeetingView from "../../../components/mediaplayer/attendee-meeting-sdk";
import PresentationViewer from "../../../components/slider/attendee-slider/ppt-view";
import SpeakerBio from "../../../components/speakerbio/speaker-bio";
import RelatedContent from "../../../components/speakerbio/related-content";
import { useParams } from "react-router-dom";
import { EventFetchSlides } from "../../../services/event/attendee-event-slides";
import Loader from "../../../utils/loader/loader"; // Import the Loader component
import { fetchPollAllResults } from "../../../services/event/pollAllResults";

interface SdkData {
  hideQna?: boolean | undefined;
  meetingNumber: string;
  password: string;
  userName: string;
  userEmail: string;
  signature: string;
  tk?: string;
}

interface Question {
  pollId: string;
  questionText: string;
  options: Record<string, string>;
  sequence: number;
}

interface PollResult {
  option: string;
  percentage: number;
  voteCount: number;
}

interface PollResultData {
  questionTitle: string;
  totalVotes: number;
  results: PollResult[];
}

const AttendeeDashboardView: React.FC<{ sdkData: SdkData; eventData: any }> = ({
  sdkData,
  eventData,
}) => {
  const { eventId } = useParams();
  const [slides, setSlides] = useState<any[]>([]);
  const [speakerBio, setSpeakerBio] = useState<any[]>([]);
  const [relatedContent, setRelatedContent] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pollQuestions, setPollQuestions] = useState<Question[]>([]);
  const [allPollResults, setAllPollResults] = useState<PollResultData[]>([]);
  const [defaultSpeakers, setDefaultSpeakers] = useState<any[]>([]);
  const [meetingHeight, setMeetingHeight] = useState(0);
  const [meetingSdkViewFlag, setMeetingSdkViewFlag] = useState<any>([]);
  const [isSlides, setIsSlides] = useState<any>([]);
  const [isRelatedContent, setIsRelatedContent] = useState<any>([]);
  const [startMeeting, setStartMeeting] = useState<any>(false);
  const [stepsEnabled, setStepsEnabled] = useState(false);
  const [responsiveMeetingHeight, setResponsiveMeetingHeight] = useState(0);
  const [isZoomSdkExpanded, setIsZoomSdkExpanded] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [qnaHeight, setQnaHeight] = useState(0);
  const [attendeeTitle, setAttendeeTitle] = useState(27);
  const [bioWrapperMarginTop, setBioWrapperMarginTop] = useState(0);
  useEffect(() => {
  }, [isLoading]);

  useEffect(() => {
    const words = eventData?.title.split(" ");
    if (words.length > 10) {
      const excessWords = words.length - 10;
      setAttendeeTitle(Math.max(22, 19 - excessWords)); // Decrease font size, but keep a minimum size
    } else {
      setAttendeeTitle(16); // Default font size
    }
  }, [eventData.title]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await EventFetchSlides(Number(eventId));
        setSlides(response);
        setSpeakerBio(response?.collaterals?.speakerbio);
        setRelatedContent(response?.collaterals?.attachments);
        setPollQuestions(response.collaterals.pollQuestions);
        setDefaultSpeakers(response.defaultSpeakers);
        setShowChatPanel(response.showChatPanel);
        // response.collaterals.slides = [];
        // If slides data is empty, set custom; otherwise set default
        if (response?.collaterals?.slides.length === 0) {
          setMeetingSdkViewFlag('custom');
          setIsSlides(false);
          setStartMeeting(true);
        } else {
          setMeetingSdkViewFlag('default');
          setIsSlides(true);
          setStartMeeting(true);
          setIsZoomSdkExpanded(response.showSdkLargeView);
        }

        if (response?.collaterals?.attachments.length === 0) {
          setIsRelatedContent(false);
        }else{
          setIsRelatedContent(true);
        }
      } catch (error) {
        console.error("Error fetching slides:", error);
      }
    };

    fetchData();
  }, [eventId]);

  useEffect(() => {
    const fetchPollResult = async () => {
      try {
        const response = await fetchPollAllResults(Number(eventId));
        setAllPollResults(response);
      } catch (error) {
        console.error("Error fetching poll results:", error);
      }
    };

    fetchPollResult();
  }, [eventId]);

  // Trigger the steps when loading is done
  useEffect(() => {
    if (!isLoading) {
      setStepsEnabled(true);
    }
  }, [isLoading]);

  const getHeightOfMeeting = (height: number) => {
    setMeetingHeight(height);
  };

  const getHeightOfMeetingResponsive = (height: number) => {
    if(meetingSdkViewFlag == 'custom'){
      setResponsiveMeetingHeight(height - 20);
    }else{
      setResponsiveMeetingHeight(height);
    }
  };

  const getHeightOfQna = (height: number) => {
    setQnaHeight(height);
  };
  
  const allSteps = [
    {
      element: 'button[aria-label="Join Audio"]',
      intro: 'Click this icon to turn on the session\'s audio and choose your speaker output.',
      scrollToElement: true,
    },
  ];

  const isMobileDevice = () => window.innerWidth <= 768; // Define the breakpoint for mobile devices

  const getHeightLiveViewResponsive = () => {
    if(window.innerWidth <= 768){
    const presentationViewer = document.getElementById("presentationViewer");
    const presentationHeight = presentationViewer?.offsetHeight || 0;
    const meetingSDKElement = document.getElementById("meetingSDKElement") as HTMLElement;
    const childElement = meetingSDKElement.querySelector('[aria-label="Zoom Web SDK Widget"]') as HTMLElement;
     if (childElement) {
        const height = childElement.clientHeight;
        setResponsiveMeetingHeight(height);
        const gaptemp = isZoomSdkExpanded ? 20 : 40;
        const marginTop = presentationHeight + height + gaptemp; // Adjust as needed
        setBioWrapperMarginTop(marginTop);
      }
    }
  };

  const calculateMarginTop = () => {
    const presentationViewer = document.getElementById("presentationViewer");
    const presentationHeight = presentationViewer?.offsetHeight || 0;
    const gaptemp = isZoomSdkExpanded ? 20 : 40;
    const marginTop = presentationHeight + responsiveMeetingHeight + gaptemp; // Adjust as needed
    if(window.innerWidth <= 768){
      setBioWrapperMarginTop(marginTop);
    }
  };

  useEffect(() => {
    // Initial calculation
    calculateMarginTop();
    // Recalculate on window resize
    window.addEventListener("resize", calculateMarginTop);
    return () => {
      window.removeEventListener("resize", calculateMarginTop);
    };
  }, [responsiveMeetingHeight]);

  let speakerMarginTop;
  let relatedContentMarginTop;

  if (isMobileDevice()) {
    speakerMarginTop = isZoomSdkExpanded ? bioWrapperMarginTop  + 20 : bioWrapperMarginTop; // Mobile-specific margin
    const bioSpeaker = document.getElementById("bio-speaker");
    const bioSpeakernHeight = bioSpeaker?.offsetHeight || 0;
    relatedContentMarginTop = isZoomSdkExpanded ? bioWrapperMarginTop + 40 :  bioSpeakernHeight + 40;
  } else {
    speakerMarginTop = !isZoomSdkExpanded ? meetingHeight + 10 : '315px';
    relatedContentMarginTop = isZoomSdkExpanded && qnaHeight ? qnaHeight + 15 : qnaHeight  + 15;
  }

  return (
    <div className="attendee-main-panel">
      {isLoading && <Loader />}
      <Steps
        enabled={stepsEnabled}
        steps={allSteps}
        initialStep={0}
        onExit={() => setStepsEnabled(false)}
      />
      <div className="attendee-header">
        <div className="event-title-attendee" style={{
              fontSize: `${attendeeTitle}px`,
              transition: "font-size 0.3s ease",
            }}>{eventData?.title}</div>
      </div>
      <div className="main-inner-wrapper">
        <div className="inner-left-side">
          <div className="attendee-meeting-wrapper" id="attendeeMeetingView">
          {isZoomSdkExpanded ? (
            <div className={`${isZoomSdkExpanded ? 'live-short-view' : ''}`} style={{
              marginTop: responsiveMeetingHeight
                ? responsiveMeetingHeight + 20
                : undefined,
            }}>
            <div id="presentationViewer" >
              <PresentationViewer
                slidesData={slides}
                pollData={pollQuestions}
                allPollResultData={allPollResults}
                email={sdkData.userEmail}
                getHeightLiveViewResponsive={getHeightLiveViewResponsive}
              />
            </div>
            </div>
          ) : (
            startMeeting && (
              <AttendeeMeetingView
                meetingNumber={sdkData.meetingNumber}
                password={sdkData.password}
                userName={sdkData.userName}
                userEmail={sdkData.userEmail}
                leaveUrl="https://example.com"
                signature={sdkData.signature}
                tk={sdkData.tk}
                setLoading={setIsLoading}
                meetingSdkViewFlag={meetingSdkViewFlag}
                getHeightOfMeeting={getHeightOfMeeting}
                hideQna={sdkData?.hideQna}
                getHeightOfMeetingResponsive={getHeightOfMeetingResponsive}
                showChat={showChatPanel}
                getHeightOfQna={getHeightOfQna}
              />
              )
            )}
            <div
              className="attendee-presentationview-wrapper"
              style={{
                marginTop: responsiveMeetingHeight
                  ? responsiveMeetingHeight + 20
                  : undefined,
              }}
            >
              {isZoomSdkExpanded ? (
                <AttendeeMeetingView
                  meetingNumber={sdkData.meetingNumber}
                  password={sdkData.password}
                  userName={sdkData.userName}
                  userEmail={sdkData.userEmail}
                  leaveUrl="https://example.com"
                  signature={sdkData.signature}
                  tk={sdkData.tk}
                  setLoading={setIsLoading}
                  meetingSdkViewFlag={meetingSdkViewFlag}
                  getHeightOfMeeting={getHeightOfMeeting}
                  hideQna={sdkData?.hideQna}
                  getHeightOfMeetingResponsive={getHeightOfMeetingResponsive}
                  isZoomSdkExpanded={isZoomSdkExpanded}
                  showChat={showChatPanel}
                  getHeightOfQna={getHeightOfQna}
                />
              ) : (
                isSlides && (
                  <div id="presentationViewer">
                    <PresentationViewer
                      slidesData={slides}
                      pollData={pollQuestions}
                      allPollResultData={allPollResults}
                      email={sdkData.userEmail}
                      getHeightLiveViewResponsive={getHeightLiveViewResponsive}
                    />
                  </div>
                )
              )}
            </div>
          </div>
          {!isLoading && !showChatPanel && (
          <div className="right-attendee-speaker-bio-wrapper" id="bio-speaker"
            style={{
              marginTop:  speakerMarginTop
            }}
          >
            <SpeakerBio speakerBio={speakerBio} defaultSpeakers={defaultSpeakers} isZoomSdkExpanded={isZoomSdkExpanded}/>
          </div>
          )}
        </div>
        {!isLoading && (
          <div className="inner-right-side">
            {showChatPanel && (
            <>
              <div className="attendee-chat-wrapper" id="attendeeattndeeChatElementwrapper">
                <div id="attendeeattndeeChatElement" className="chat-panel"></div>
              </div>
              <div className="attendee-speaker-bio-wrapper" id="bio-speaker">
                <SpeakerBio speakerBio={speakerBio} defaultSpeakers={defaultSpeakers} />
              </div>
            </>
            )}
            { sdkData?.hideQna && isRelatedContent && (
              <div
                className={`related-content-wrapper custom-related-content-qa ${meetingSdkViewFlag === 'custom' && meetingHeight && 'custom-related-content-qa'}`}
                id="relatedContent"
                style={{
                  marginTop: meetingSdkViewFlag === 'custom' && meetingHeight ? meetingHeight + 10 : undefined,
                  left: meetingSdkViewFlag === 'custom' && meetingHeight ? '450px' : undefined,
                  width: meetingSdkViewFlag === 'custom' && meetingHeight ? 'calc(100vw - 760px)' : undefined,
                }}
              >
                <RelatedContent relatedContent={relatedContent} />
              </div> )}
              {!sdkData?.hideQna && isRelatedContent && (
                <div
                  className={`related-content-wrapper ${meetingSdkViewFlag === 'custom' && meetingHeight && 'custom-related-content'}`}
                  id="relatedContent"
                  style={{
                    marginTop: relatedContentMarginTop,
                    width: '285px',
                  }}
                >
                  <RelatedContent relatedContent={relatedContent} />
                </div>
              )}
          </div>
        )}
      </div>
    </div >
  );
};

export default AttendeeDashboardView;
